import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Loader2, ShieldCheck, CreditCard } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { INTERVALS, TRIAL_DAYS } from "@/lib/plans";
import { IntervalSwitch } from "@/components/PricingSection";

let sdkPromise = null;
const loadSdk = (clientId) => {
  const src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription&enable-funding=card&components=buttons`;
  if (window.paypal) return Promise.resolve(window.paypal);
  sdkPromise =
    sdkPromise ||
    new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = src;
      s.onload = () => resolve(window.paypal);
      s.onerror = () => {
        sdkPromise = null;
        reject(new Error("Could not load PayPal"));
      };
      document.body.appendChild(s);
    });
  return sdkPromise;
};

const PayButtons = ({ interval, onSuccess }) => {
  const paypalRef = useRef(null);
  const cardRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let buttons = [];
    let cancelled = false;
    api
      .billingStatus()
      .then((b) => (b.client_id ? loadSdk(b.client_id) : null))
      .then((paypal) => {
        if (cancelled) return;
        if (!paypal) return setStatus("unavailable");
        const opts = (fundingSource) => ({
          fundingSource,
          style: { layout: "vertical", shape: "rect", color: fundingSource === paypal.FUNDING.CARD ? "black" : "gold", label: "subscribe", height: 48 },
          createSubscription: async () => (await api.subscribe(window.location.origin, interval)).subscription_id,
          onApprove: async (data) => onSuccess(await api.confirmSubscription(data.subscriptionID)),
          onError: (err) => toast.error(err?.message || "PayPal could not complete the payment"),
        });
        buttons = [
          [paypal.FUNDING.PAYPAL, paypalRef],
          [paypal.FUNDING.CARD, cardRef],
        ].map(([src, ref]) => {
          const b = paypal.Buttons(opts(src));
          if (!cancelled && b.isEligible() && ref.current) b.render(ref.current).catch(() => {});
          return b;
        });
        setStatus("ready");
      })
      .catch((e) => setStatus(errMsg(e)));
    return () => {
      cancelled = true;
      buttons.forEach((b) => b.close?.().catch(() => {}));
    };
  }, [interval, onSuccess]);

  return (
    <div data-testid="paypal-buttons" className="space-y-3">
      {status === "loading" && (
        <p className="flex items-center gap-2 text-sm text-stone-400"><Loader2 size={14} className="animate-spin" /> Loading secure checkout…</p>
      )}
      {status === "unavailable" && <p className="text-sm text-stone-500" data-testid="paypal-unavailable">PayPal checkout is not available right now.</p>}
      {!["loading", "ready", "unavailable"].includes(status) && <p className="text-sm text-[#E11D48]" data-testid="paypal-error">{status}</p>}
      <div ref={paypalRef} data-testid="paypal-button-paypal" />
      <div ref={cardRef} data-testid="paypal-button-card" />
    </div>
  );
};

const CodeBox = ({ promo, setPromo, onFree }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.redeemCode(code, window.location.origin);
      if (r.kind === "free_months") return onFree(r);
      setPromo(r);
      toast.success(`${r.percent_off}% off for ${r.months} month${r.months > 1 ? "s" : ""} applied`);
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  if (promo)
    return (
      <p className="text-sm text-amber-300 flex items-center justify-between border border-[#D99B43]/40 px-3 py-2" data-testid="checkout-promo-applied">
        <span>{promo.code}: {promo.percent_off}% off for {promo.months} month{promo.months > 1 ? "s" : ""} (card checkout)</span>
        <button onClick={() => setPromo(null)} className="text-stone-400 hover:text-stone-200 text-xs" data-testid="checkout-promo-remove">Remove</button>
      </p>
    );
  return (
    <form onSubmit={apply} className="flex gap-2" data-testid="checkout-code-form">
      <input data-testid="checkout-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Gift or discount code" maxLength={24}
        className="flex-1 bg-[#0f0d11] border border-[#352d40] px-3 py-2 text-sm uppercase outline-none focus:border-[#D99B43]" />
      <button data-testid="checkout-code-apply" disabled={busy || !code.trim()} className="btn-gothic">{busy ? <Loader2 size={14} className="animate-spin" /> : "Apply"}</button>
    </form>
  );
};

const StripeButton = ({ interval, promo }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      window.location.href = (await api.stripeCheckout(window.location.origin, interval, promo?.code)).checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <button data-testid="stripe-checkout-btn" onClick={go} disabled={busy} className="btn-crimson w-full justify-center !py-3.5">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Pay with card or wallet
    </button>
  );
};

export default function Checkout() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { setUser, isPro, trialEligible, billingReady } = useAuth();
  const [interval, setIntervalState] = useState(params.get("interval") === "year" ? "year" : "month");
  const setInterval = (v) => {
    setIntervalState(v);
    window.history.replaceState(null, "", `/checkout?interval=${v}`);
  };
  const [promo, setPromo] = useState(null);
  const full = interval === "year" ? 90 : 9;
  const off = promo ? (full * (100 - promo.percent_off)) / 100 : full;
  const fmt = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
  const discounted =
    promo &&
    (interval === "year"
      ? `${fmt(off)} for your first year (${promo.percent_off}% off), then $90/year`
      : `${fmt(off)}/month for ${promo.months} month${promo.months > 1 ? "s" : ""} (${promo.percent_off}% off), then $9/month`);
  const onFree = (r) => {
    setUser(r.user);
    toast.success(`Code accepted: ${r.months} month${r.months > 1 ? "s" : ""} of Pro, on the house`);
    nav("/library", { replace: true });
  };
  const iv = INTERVALS[interval];
  const onSuccess = useRef((user) => {
    setUser(user);
    toast.success("Welcome to Pro. The full choir is yours.");
    nav("/library", { replace: true });
  }).current;

  if (isPro) return <div className="min-h-screen bg-[#080708] flex items-center justify-center"><Link to="/library" className="btn-gothic">You are already Pro, open your crypt</Link></div>;

  return (
    <div className="min-h-screen bg-[#080708] flex items-center justify-center px-6 py-16" data-testid="checkout-page">
      <div className="w-full max-w-md stone-panel arch-top p-8 rise tube-glow">
        <Link to="/" className="font-gothic text-lg tracking-[0.15em] text-amber-100/90">VOX <span className="text-[#c92a3e]">✦</span> NOCTURNA</Link>
        <h1 className="font-gothic text-2xl uppercase tracking-wider text-stone-100 mt-6 flex items-center gap-2"><Crown size={20} className="text-[#D99B43]" /> Vox Nocturna Pro</h1>
        <div className="mt-5"><IntervalSwitch value={interval} onChange={setInterval} testId="checkout-interval" /></div>
        <div className="border border-[#352d40] p-4 mt-5 space-y-1" data-testid="checkout-summary">
          {!billingReady ? (
            <div className="h-14 bg-[#352d40]/40 animate-pulse" data-testid="checkout-summary-loading" />
          ) : trialEligible ? (
            <>
              <p className="text-stone-100"><span className="font-gothic text-3xl">$0</span> today · {TRIAL_DAYS} days free</p>
              <p className="text-sm text-stone-400">Then {promo ? discounted : `${iv.price}/${iv.per}${iv.note ? ` (${iv.note})` : ""}`}, plus applicable tax, billed automatically. Cancel anytime.</p>
            </>
          ) : promo ? (
            <p className="text-stone-100" data-testid="checkout-discounted-price">{discounted} <span className="text-sm text-stone-400">plus applicable tax</span></p>
          ) : (
            <p className="text-stone-100"><span className="font-gothic text-3xl">{iv.price}</span> / {iv.per} <span className="text-sm text-stone-400">{iv.note}</span></p>
          )}
        </div>
        <p className="eyebrow mt-6 mb-3">Card, Apple Pay or Google Pay · secured by Stripe</p>
        <div className="mb-4"><CodeBox promo={promo} setPromo={setPromo} onFree={onFree} /></div>
        <StripeButton interval={interval} promo={promo} />
        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-[#352d40]" />
          <span className="eyebrow">or pay with PayPal</span>
          <span className="flex-1 h-px bg-[#352d40]" />
        </div>
        {promo ? (
          <p className="text-xs text-stone-500 border border-[#352d40] px-3 py-2" data-testid="checkout-paypal-hidden-note">
            PayPal doesn't support discount codes. Remove the code to pay full price with PayPal.
          </p>
        ) : (
          <div className="bg-[#f4f1ec] p-4 rounded-sm">
            <PayButtons key={interval} interval={interval} onSuccess={onSuccess} />
          </div>
        )}
        <p className="text-[11px] text-stone-500 mt-4 flex items-center gap-1.5"><ShieldCheck size={13} /> Payments are processed securely by Stripe or PayPal. We never see your card details.</p>
      </div>
    </div>
  );
}
